import { Vec3 } from '@/core/math/Vec3';
import { exhaustVelocity, g0 } from '@/core/units';
import { CelestialBody } from '@/universe/CelestialBody';
import {
  circularVelocity,
  elementsFromState,
  stateVectorAt,
  timeToApoapsis,
  timeToPeriapsis,
} from '@/universe/OrbitalElements';
import { Spacecraft } from './Spacecraft';
import { TrajectoryPredictor, TrajectoryPrediction } from './TrajectoryPredictor';

/** The orbital (prograde / normal / radial) basis at a state vector. */
export interface OrbitalBasis {
  prograde: Vec3;
  normal: Vec3;
  radial: Vec3;
}

/**
 * The orbital basis at a state vector.
 *
 * Radial is re-orthogonalised against prograde so the basis stays exact on an
 * eccentric orbit, where position and velocity are not perpendicular.
 */
export function orbitalBasis(position: Vec3, velocity: Vec3): OrbitalBasis {
  const prograde = velocity.lengthSq() > 1e-12 ? velocity.normalized() : new Vec3(0, 0, 1);
  const radialOut = position.lengthSq() > 1e-12 ? position.normalized() : new Vec3(0, 1, 0);
  let normal = Vec3.crossVectors(position, velocity);
  if (normal.lengthSq() < 1e-12) normal = Vec3.anyPerpendicular(prograde);
  normal.normalize();
  const radial = radialOut.rejectFromUnit(prograde).normalize();
  return { prograde, normal, radial };
}

/** Expands prograde/normal/radial components into an inertial-frame vector. */
export function orbitalBasisDeltaV(
  position: Vec3,
  velocity: Vec3,
  prograde: number,
  normal: number,
  radial: number,
): Vec3 {
  const basis = orbitalBasis(position, velocity);
  return new Vec3()
    .addScaled(basis.prograde, prograde)
    .addScaled(basis.normal, normal)
    .addScaled(basis.radial, radial);
}

/**
 * A planned velocity change, expressed in the orbital (prograde / normal /
 * radial) basis at the burn point. Storing it in that basis rather than as a
 * fixed vector is what makes a node stay meaningful when the orbit underneath
 * it changes.
 */
export interface ManeuverNode {
  id: string;
  /** Simulation time at which the burn should be centred, seconds. */
  time: number;
  /** Along the velocity vector, m/s. Negative is retrograde. */
  prograde: number;
  /** Along the orbit normal, m/s. Negative is anti-normal. */
  normal: number;
  /** Away from the body centre, m/s. Negative is radial-in. */
  radial: number;
  label: string;
}

export interface BurnEstimate {
  /** Magnitude of the velocity change, m/s. */
  deltaV: number;
  /** Time the burn will take at full available thrust, seconds. */
  duration: number;
  /** When to start the burn so it is centred on the node, seconds. */
  startTime: number;
  /** Propellant required, kg. */
  propellant: number;
  /** Peak acceleration during the burn, m/s^2. */
  peakAcceleration: number;
  /** True if the vessel does not have enough delta-v for the node. */
  insufficientDeltaV: boolean;
  /** True if no engine can produce useful thrust. */
  noThrust: boolean;
}

/**
 * Manoeuvre planning and execution.
 *
 * A node describes a velocity change, not a state change. Executing it means
 * pointing the vessel and running an engine for a finite time, which consumes
 * propellant and mass and therefore changes the answer slightly as it goes.
 * The planner accounts for that by recomputing the remaining delta-v every step
 * rather than by teleporting the vessel onto the predicted orbit.
 */
export class ManeuverPlanner {
  readonly nodes: ManeuverNode[] = [];
  private nextId = 1;

  /** Node currently being executed, if any. */
  executingNodeId: string | null = null;
  /** Remaining delta-v on the executing node, m/s. */
  remainingDeltaV = 0;
  /** True while the flight computer is holding the burn attitude. */
  aligning = false;

  constructor(private readonly predictor: TrajectoryPredictor) {}

  add(node: Omit<ManeuverNode, 'id'>): ManeuverNode {
    const created: ManeuverNode = { ...node, id: `node-${this.nextId++}` };
    this.nodes.push(created);
    this.nodes.sort((a, b) => a.time - b.time);
    return created;
  }

  remove(id: string): void {
    const i = this.nodes.findIndex((n) => n.id === id);
    if (i >= 0) this.nodes.splice(i, 1);
    if (this.executingNodeId === id) this.cancelExecution();
  }

  clear(): void {
    this.nodes.length = 0;
    this.cancelExecution();
  }

  get next(): ManeuverNode | null {
    return this.nodes.length > 0 ? this.nodes[0] : null;
  }

  /**
   * Converts a node's orbital-basis components into a velocity vector in the
   * reference body's inertial frame at the node's time.
   */
  deltaVVector(node: ManeuverNode, body: CelestialBody, position: Vec3, velocity: Vec3, t0: number): Vec3 {
    const elements = elementsFromState(position, velocity, body.mu, t0);
    const atNode = stateVectorAt(elements, node.time);
    return this.basisDeltaV(node, atNode.position, atNode.velocity);
  }

  private basisDeltaV(node: ManeuverNode, position: Vec3, velocity: Vec3): Vec3 {
    return orbitalBasisDeltaV(position, velocity, node.prograde, node.normal, node.radial);
  }

  /** Estimates burn duration, propellant and feasibility. */
  estimate(node: ManeuverNode, ship: Spacecraft): BurnEstimate {
    const dv = Math.hypot(node.prograde, node.normal, node.radial);

    // Use the best-thrust non-RCS engine group that has propellant.
    let thrust = 0;
    let isp = 0;
    for (const e of ship.mainEngines) {
      if (ship.fuelOfType(e.def.fuelType) <= 0) continue;
      thrust += e.def.maxThrust;
      isp = Math.max(isp, e.def.ispVacuum);
    }

    if (thrust <= 0 || isp <= 0) {
      return {
        deltaV: dv,
        duration: Infinity,
        startTime: node.time,
        propellant: Infinity,
        peakAcceleration: 0,
        insufficientDeltaV: true,
        noThrust: true,
      };
    }

    const ve = exhaustVelocity(isp);
    const m0 = ship.massProperties.total;
    const mf = m0 * Math.exp(-dv / ve);
    const propellant = m0 - mf;
    const flow = thrust / (isp * g0);
    const duration = flow > 0 ? propellant / flow : Infinity;
    const available = ship.totalDeltaV();

    return {
      deltaV: dv,
      duration,
      startTime: node.time - duration / 2,
      propellant,
      peakAcceleration: thrust / Math.max(1, mf),
      insufficientDeltaV: dv > available,
      noThrust: false,
    };
  }

  /** Trajectory that results from executing a node. */
  preview(
    node: ManeuverNode,
    body: CelestialBody,
    position: Vec3,
    velocity: Vec3,
    t0: number,
    terrainCeiling: number,
  ): TrajectoryPrediction {
    const dv = this.deltaVVector(node, body, position, velocity, t0);
    return this.predictor.predictAfterBurn(body, position, velocity, t0, node.time, dv, { terrainCeiling });
  }

  beginExecution(node: ManeuverNode): void {
    this.executingNodeId = node.id;
    this.remainingDeltaV = Math.hypot(node.prograde, node.normal, node.radial);
    this.aligning = true;
  }

  cancelExecution(): void {
    this.executingNodeId = null;
    this.remainingDeltaV = 0;
    this.aligning = false;
  }

  /**
   * Advances node execution by one step.
   *
   * Returns the direction the nose should point and the throttle to command.
   * The caller feeds these into the flight computer, which turns them into
   * actual actuator commands. Nothing here writes the vessel's state.
   */
  updateExecution(
    ship: Spacecraft,
    body: CelestialBody,
    t: number,
    dt: number,
  ): { direction: Vec3 | null; throttle: number; complete: boolean } {
    const node = this.nodes.find((n) => n.id === this.executingNodeId);
    if (!node) return { direction: null, throttle: 0, complete: true };

    const direction = this.basisDeltaV(node, ship.body.position, ship.body.velocity);
    const dvLen = direction.length();
    if (dvLen < 1e-9) {
      this.remove(node.id);
      return { direction: null, throttle: 0, complete: true };
    }
    direction.mulScalar(1 / dvLen);

    const estimate = this.estimate(node, ship);
    const burnStart = node.time - estimate.duration / 2;

    // Hold attitude until the burn window opens.
    if (t < burnStart) {
      this.aligning = true;
      return { direction, throttle: 0, complete: false };
    }
    this.aligning = false;

    // Track how much delta-v has actually been delivered by integrating the
    // thrust acceleration, rather than assuming the plan was followed exactly.
    const accel = ship.lastThrustTotal / Math.max(1, ship.massProperties.total);
    const alignment = Math.max(0, ship.body.forward().dot(direction));
    this.remainingDeltaV -= accel * alignment * dt;

    if (this.remainingDeltaV <= 0.05) {
      this.remove(node.id);
      this.cancelExecution();
      return { direction, throttle: 0, complete: true };
    }

    // Taper the throttle at the end so the burn does not overshoot by a full
    // control period's worth of acceleration.
    const stoppingDv = accel * dt * 4;
    const throttle = this.remainingDeltaV < stoppingDv ? Math.max(0.08, this.remainingDeltaV / Math.max(1e-3, stoppingDv)) : 1;

    void body;
    return { direction, throttle, complete: false };
  }

  // ---- Node builders -------------------------------------------------------

  /**
   * Builds a node that circularises the orbit at the next apoapsis (or
   * periapsis, whichever is chosen).
   */
  planCircularisation(
    ship: Spacecraft,
    body: CelestialBody,
    t: number,
    at: 'apoapsis' | 'periapsis' = 'apoapsis',
  ): ManeuverNode | null {
    const elements = elementsFromState(ship.body.position, ship.body.velocity, body.mu, t);
    const dtToNode = at === 'apoapsis' ? timeToApoapsis(elements, t) : timeToPeriapsis(elements, t);
    if (!Number.isFinite(dtToNode)) return null;

    const nodeTime = t + dtToNode;
    const sv = stateVectorAt(elements, nodeTime);
    const r = sv.position.length();
    const currentSpeed = sv.velocity.length();
    const targetSpeed = circularVelocity(body.mu, r);

    // At an apsis the velocity is purely horizontal, so the required change is
    // entirely prograde or retrograde.
    return this.add({
      time: nodeTime,
      prograde: targetSpeed - currentSpeed,
      normal: 0,
      radial: 0,
      label: `Circularise at ${at}`,
    });
  }

  /** Builds a node that raises or lowers the opposite apsis to a target radius. */
  planApsisChange(
    ship: Spacecraft,
    body: CelestialBody,
    t: number,
    targetAltitude: number,
    at: 'apoapsis' | 'periapsis' = 'periapsis',
  ): ManeuverNode | null {
    const elements = elementsFromState(ship.body.position, ship.body.velocity, body.mu, t);
    const dtToNode = at === 'apoapsis' ? timeToApoapsis(elements, t) : timeToPeriapsis(elements, t);
    if (!Number.isFinite(dtToNode)) return null;

    const nodeTime = t + dtToNode;
    const sv = stateVectorAt(elements, nodeTime);
    const r1 = sv.position.length();
    const r2 = body.radius + targetAltitude;

    // Vis-viva on the transfer ellipse with apsides r1 and r2.
    const aNew = (r1 + r2) / 2;
    const vNew = Math.sqrt(body.mu * (2 / r1 - 1 / aNew));
    const vNow = sv.velocity.length();

    return this.add({
      time: nodeTime,
      prograde: vNew - vNow,
      normal: 0,
      radial: 0,
      label: `${r2 > r1 ? 'Raise' : 'Lower'} opposite apsis to ${(targetAltitude / 1000).toFixed(0)} km`,
    });
  }

  /**
   * Plans a Hohmann-style transfer to a coplanar target orbit radius, returning
   * the departure node. The arrival circularisation is planned separately once
   * the transfer is under way, because by then the vessel's actual state is
   * known rather than assumed.
   */
  planHohmannDeparture(
    ship: Spacecraft,
    body: CelestialBody,
    t: number,
    targetRadius: number,
  ): ManeuverNode | null {
    const elements = elementsFromState(ship.body.position, ship.body.velocity, body.mu, t);
    const r1 = ship.body.position.length();
    const v1 = Math.sqrt(body.mu / r1);
    const aTransfer = (r1 + targetRadius) / 2;
    const vTransfer = Math.sqrt(body.mu * (2 / r1 - 1 / aTransfer));

    void elements;
    return this.add({
      time: t + 30,
      prograde: vTransfer - v1,
      normal: 0,
      radial: 0,
      label: `Transfer to ${(targetRadius / 1000).toFixed(0)} km`,
    });
  }
}
